import { Children, createElement, useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import type { ReactElement, ReactNode } from 'react';

export interface FormItemProps {
  children:
    | ReactElement
    | ReactElement[]
    | (({ onChange, name, value }: any) => ReactElement);
  label: string;
  helpText?: string | ReactNode;
  name: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
}

function Item({
  children,
  label,
  helpText,
  name,
  required = false,
  error,
  errorMessage,
}: FormItemProps) {
  const {
    register,
    watch,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  useEffect(() => {
    if (error) {
      setError(name, { type: 'manual', message: errorMessage });
    } else {
      clearErrors(name);
    }
  }, [error]);

  const formError = errors[name];

  const isFaCC = typeof children === 'function';

  const value = isFaCC && watch(name);

  const registerReturn = register(name, { required });

  return (
    <div>
      <div className="flex justify-between">
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700"
        >
          {label}
        </label>
        {!required && <span className="text-sm text-gray-500">Optional</span>}
      </div>
      <div className="mt-1">
        {isFaCC
          ? children({
              value,
              ...registerReturn,
            })
          : Children.map(children, (child, i) => {
              return createElement(child.type, {
                ...child.props,
                hasError: !!formError,
                ...registerReturn,
                key: i,
              });
            })}
      </div>
      {helpText && !formError && (
        <p className="mt-2 mb-4 text-sm text-gray-500">{helpText}</p>
      )}
      {formError && (
        <p className="mt-2 mb-4 text-sm text-red-600">
          {formError.type === 'required' && 'This field is required'}
          {formError.type === 'manual' && formError.message?.toString()}
        </p>
      )}
    </div>
  );
}

export interface FormProps {
  className?: string;
  children: ReactNode;
  onSubmit: any;
  defaultValues?: any;
}

export function Form({
  className,
  defaultValues = {},
  children,
  onSubmit,
}: FormProps) {
  const methods = useForm({ defaultValues });

  return (
    <FormProvider {...methods}>
      <form className={className} onSubmit={methods.handleSubmit(onSubmit)}>
        {children}
      </form>
    </FormProvider>
  );
}

Form.Item = Item;

Form.displayName = 'Form';

export default Form;
